import { Context } from 'bernie-context';
import { ExperimentStore } from 'bernie-core';
import { Logger } from 'bernie-logger';
import { PageData } from 'bernie-http';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

import { BffResponseRetrofitAdapter, MigrationExperimentMapping, SearchMigrationAdapter } from 'stores/adapters/bex-api/migration-adapters';
import { getDateStringFromDateRange } from 'stores/adapters';
import { STORE_EVENTS } from 'stores/events/store-events';

import { HotelResultsViewModel } from 'models';

import { FullPropertySearch, PropertyMapMigration, PropertySearch } from '__generated__/api';
import { OperationResult } from '__generated__/api/api-operations';

export const searchMigrationAdaptorExecutor = (
  context: Context,
  pageData: PageData,
  searchMigrationAdapters: SearchMigrationAdapter[],
  searchMigrationExperimentMapping: MigrationExperimentMapping,
  bffResponseRetrofitAdapters: BffResponseRetrofitAdapter[],
  experiments: ExperimentStore,
  bffResults: HotelResultsViewModel,
  bexApiResults:
    | OperationResult<PropertySearch.Query>
    | OperationResult<FullPropertySearch.Query>
    | OperationResult<PropertyMapMigration.Query>,
  logger: Logger
): HotelResultsViewModel => {
  let propertySearchResults: HotelResultsViewModel = { ...bffResults };

  // Run retrofit adapters to conform parts of the BFF response to fit the shape of parts of the BEX-API response.
  bffResponseRetrofitAdapters.forEach(adapter => {
    propertySearchResults = adapter(propertySearchResults);
  });

  if (!bexApiResults || !bexApiResults.data) {
    if (logger && typeof logger.logEvent === 'function') {
      logger.logEvent(STORE_EVENTS.BEX_API_SEARCH_RESULTS_BFF_FALLBACK);
    }

    return propertySearchResults;
  }

  // Run migrated adapters.
  searchMigrationAdapters.forEach(adapter => {
    propertySearchResults = adapter(propertySearchResults, bexApiResults);
  });

  if (!searchMigrationExperimentMapping) {
    return propertySearchResults;
  }

  // Run experimental adapters.
  let bexApiPropertyDict: { [key: string]: PropertyMapMigration.Properties };
  Object.keys(searchMigrationExperimentMapping).forEach(key => {
    const exposure = experiments.exposures[key];

    // Comment out if without experiment.
    if (!exposure) {
      return;
    }

    experiments.logExperiment(context, pageData, exposure);

    // ABBA test setup. 0=control,1=variant,2=variant,3=control
    if (exposure.bucket !== 1 && exposure.bucket !== 2) {
      return;
    }

    const { data } = bexApiResults;
    const { propertySearch } = data;
    if (!bexApiPropertyDict && propertySearch) {
      const bexApiPropertySearch = propertySearch as PropertyMapMigration.PropertySearch | FullPropertySearch.PropertySearch;
      const bexApiProperties = bexApiPropertySearch.properties;

      // Comparing Date Range for BEXAPi search Criteria with bff Search criteria in case of Property Map Migration and Full Property Search
      // and rejecting other adapter except `shopping_pwa_mm_bex_api_lodging_properties_baseline` to allow `MapAndProperties` to be still
      // consumed from bex-api. Check search-migration-adapters.ts
      if (shouldFallBackToBFFData(bexApiPropertySearch, bffResults, logger, key)) {
        return;
      }

      if (bexApiProperties) {
        bexApiPropertyDict = {};
        bexApiProperties.forEach(property => {
          const propertyKey = getPropertyKey(property.id, property.sponsoredListing);
          bexApiPropertyDict[propertyKey] = property;
        });
      }
    }

    propertySearchResults = searchMigrationExperimentMapping[key](propertySearchResults, bexApiResults, bexApiPropertyDict);
  });

  return propertySearchResults;
};

function shouldFallBackToBFFData(
  propertySearch: PropertyMapMigration.PropertySearch | FullPropertySearch.PropertySearch,
  bffResults: HotelResultsViewModel,
  logger: Logger,
  experimentKey: string
) {
  const dateRange = propertySearch.searchCriteria.dateRange;
  const bexApiCheckin = getDateStringFromDateRange(dateRange.checkInDate);
  const bexApiCheckout = getDateStringFromDateRange(dateRange.checkOutDate);
  const bffCheckIn =
    bffResults.defaultRequestFields && bffResults.defaultRequestFields.startDate ? bffResults.defaultRequestFields.startDate : null;
  const bffCheckOut =
    bffResults.defaultRequestFields && bffResults.defaultRequestFields.endDate ? bffResults.defaultRequestFields.endDate : null;

  if (
    bffCheckIn &&
    bffCheckOut &&
    (bexApiCheckin !== bffCheckIn || bexApiCheckout !== bffCheckOut) &&
    experimentKey !== 'shopping_pwa_mm_bex_api_lodging_properties_baseline'
  ) {
    if (logger && typeof logger.logEvent === 'function') {
      logger.logEvent(
        new SystemEvent(SystemEventLevel.INFO, 'BEX_API_SEARCH_RESULTS_BFF_DATES_FALLBACK'),
        `bffStartDate=${bffCheckIn} bffEndDate=${bffCheckOut} ` + `bexApiCheckin=${bexApiCheckin} bexApiCheckout=${bexApiCheckout}`
      );
    }

    return true;
  }

  return false;
}

export const getPropertyKey = (propertyId, sponsoredListing) => (sponsoredListing ? `${propertyId}-ad` : propertyId);
